/* eslint-disable global-require */
import mapValues from 'lodash/mapValues';
import get from 'lodash/get';

import moulages from '~lib/common/config/moulages';
import featureAvailabilityService from '~lib/common/providers/featureAvailabilityService';

import MedicationGivenCheck from './controls/MedicationGivenCheck';
import MedicationGroupCheck from './controls/MedicationGroupCheck';
import InfusionCheck from './controls/InfusionCheck';
import InfusionSetupCheck from './controls/InfusionSetupCheck';
import InfusionRateCheck from './controls/InfusionRateCheck';
import InfusionFunctionCheck from './controls/InfusionFunctionCheck';
import OxygenGivenCheck from './controls/OxygenGivenCheck';
import CompressionCheck from './controls/CompressionCheck';
import IVPlacedCheck from './controls/IVPlacedCheck';
import InterventionCheck from './controls/InterventionCheck';
import VagalManeuverCheck from './controls/VagalManeuverCheck';
import PenlightCheck from './controls/PenlightCheck';
import NasalSwabCheck from './controls/NasalSwabCheck';
import PhysicalAssessmentCheck from './controls/PhysicalAssessmentCheck';
import MoulageCheck from './controls/MoulageCheck';
import MonitorActiveCheck from './controls/MonitorActiveCheck';
import PacingCheck from './controls/PacingCheck';
import DefibCheck from './controls/DefibCheck';
import VitalAssessedCheck from './controls/VitalAssessedCheck';
import BloodPressureTakenCheck from './controls/BloodPressureTakenCheck';
import DialogueCheck from './controls/DialogueCheck';
import CallCheck from './controls/CallCheck';
import VitalCheck from './controls/VitalCheck';
import PulseStrengthCheck from './controls/PulseStrengthCheck';
import PulsePalpatedCheck from './controls/PulsePalpatedCheck';
import BehaviourCheck from './controls/BehaviourCheck';
import HeartSoundCheck from './controls/HeartSoundCheck';
import LungSoundCheck from './controls/LungSoundCheck';
import CustomStateCheck from './controls/CustomStateCheck';
import PatientTransferCheck from './controls/PatientTransferCheck';
import PPEAppliedCheck from './controls/PPEAppliedCheck';
import LabResultCheck from './controls/LabResultCheck';
import BedRailCheck from './controls/BedRailCheck';
import ExposeCheck from './controls/ExposeCheck';
import LungSoundAssessedCheck from './controls/LungSoundAssessedCheck';
import HeartSoundAssessedCheck from './controls/HeartSoundAssessedCheck';
import LabMediaAssessedCheck from './controls/LabMediaAssessedCheck';
import ComplexionCheck from './controls/ComplexionCheck';
import SpinalAssessmentCheck from './controls/SpinalAssessmentCheck';
import CustomMenuCheck from './controls/CustomMenuCheck';
import UnsolicitedCalleeCheck from './controls/UnsolicitedCalleeCheck';
import NonreactiveCheck from './controls/NonreactiveCheck';
import makeVueControl from './utils/makeVueControl';
import getEnabledRelations from './utils/getEnabledRelations';
import * as ControlUtils from './utils/controlUtils';

function infusionCheckRecommended(caseConf) {
  return (caseConf.dosageForms &&
    caseConf.dosageForms.find(caseDosageForm =>
      caseDosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION'),
    )) ||
  (caseConf.fluids && caseConf.fluids.length);
}

const CHECKS = {
  QUESTION: {
    title: 'Question',
    label: 'If patient was asked',
    component: DialogueCheck,
    options: {
      props: {
        checkType: 'QUESTION',
        placeholder: 'Question',
        verb: 'asked',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.questions,
        getDialogueItemContent: (dialogueItem) => dialogueItem.content,
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areQuestionsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
   category: 'Dialogue',
    superCategory: 'Dialogue',
  },
  AI_QUESTION: {
    title: 'Question',
    label: 'If question/statement was said',
    component: DialogueCheck,
    options: {
      props: {
        checkType: 'AI_QUESTION',
        placeholder: 'Question/Statement',
        verb: 'said',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.aiQuestions,
        getDialogueItemContent: (dialogueItem) => dialogueItem.content,
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areAiQuestionsRecommended,
    isDisabled: ControlUtils.isAiDisabled,
    category: 'Dialogue',
    superCategory: 'Dialogue',
  },
  COMMAND_COUNT: {
    title: 'Command',
    label: 'If command was given',
    component: DialogueCheck,
    options: {
      props: {
        checkType: 'COMMAND_COUNT',
        placeholder: 'Command',
        verb: 'given',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.commands,
        getDialogueItemContent: (dialogueItem) => dialogueItem.content,
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areCommandsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Dialogue',
  },
  UNSOLICITED_DIALOGUE: {
    title: 'Unsolicited',
    label: 'If unsolicited patient dialogue was played',
    component: DialogueCheck,
    options: {
      props: {
        checkType: 'UNSOLICITED_DIALOGUE',
        placeholder: 'Unsolicited',
        verb: 'played',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.unsolicited,
        getDialogueItemContent: (dialogueItem) => dialogueItem.options.answer
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areUnsolicitedRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Dialogue',
  },
  CALL: {
    title: 'Call',
    label: 'If call dialogue was said',
    component: CallCheck,
    options: {
    },
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf.dialogue && caseConf.dialogue.calls && caseConf.dialogue.calls.length,
    category: 'Dialogue',
    superCategory: 'Dialogue',
  },
  OXYGEN_ADMINISTERED: {
    title: 'Oxygen',
    label: 'If oxygen being given',
    component: OxygenGivenCheck,
    options: {
      query: require('~lib/common/queries/Oxygen').fetch,
      queryProp: 'oxygens',
    },
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_oxygens').length,
    category: 'Oxygen',
    superCategory: 'Oxygen',
  },
  VITAL_ASSESS_MONITOR: {
    title: 'Vital Assess',
    label: 'If monitoring',
    component: MonitorActiveCheck,
    options: {
      query: require('~lib/common/queries/Treatment').fetch,
      queryProp: 'treatments',
    },
    type: 'VITAL_ASSESS',
    isApplicable: check => ['ECG', 'ETCO2', 'SPO2', 'HEART_RATE', null, undefined].includes(check.id),
    category: 'Monitors',
    superCategory: 'Monitors',
  },
  LEAD_ASSESS: {
    title: 'Lead Select',
    label: 'If monitoring',
    component: MonitorActiveCheck,
    options: {
      query: require('~lib/common/queries/Treatment').fetch,
      queryProp: 'treatments',
    },
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'LEAD_SELECT'),
    category: 'Monitors',
    superCategory: 'Monitors',
  },
  IV_PLACED: {
    title: 'IV',
    label: 'If IV being placed',
    component: IVPlacedCheck,
    options: {
      props: {
        checkType: 'IV_PLACED',
      }
    },
    isRecommended: (caseConf) => caseConf.iv.enabled,
    category: 'IV',
    superCategory: 'IV',
  },
  IV_REMOVED: {
    title: 'IV',
    label: 'If IV being removed',
    component: IVPlacedCheck,
    options: {
      props: {
        checkType: 'IV_REMOVED'
      }
    },
    isRecommended: (caseConf) => caseConf.iv.enabled,
    category: 'IV',
    superCategory: 'IV',
  },
  INTERVENTION_ADMINISTERED: {
    title: 'Intervention',
    label: 'If intervention',
    component: InterventionCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    isRecommended: (caseConf) => !!caseConf.availableInterventions && !!caseConf.availableInterventions.length,
    category: 'Intervention',
    superCategory: 'Procedures',
  },
  VITAL: {
    title: 'Vital',
    label: 'If the value of a vital',
    component: VitalCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'vital',
        type: 'VITAL',
      },
    },
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  PULSE_STRENGTH: {
    title: 'Pulse Strength',
    label: 'If pulse strength is',
    component: PulseStrengthCheck,
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  PULSE_PALPATED: {
    title: 'Pulse Palpated',
    label: 'If pulse was palpated',
    component: PulsePalpatedCheck,
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  LAB_RESULT_INTERNAL: {
    title: 'Glucose',
    label: 'If glucose is',
    component: LabResultCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'labResult',
        type: 'LAB_RESULT_INTERNAL',
        useDefault: true,
      },
    },
    category: featureAvailabilityService.translateLabel('Media'),
    superCategory: 'Monitors',
  },
  VITAL_ASSESSED_BLOOD_PRESSURE: {
    title: 'BP',
    label: 'If blood pressure was taken',
    component: BloodPressureTakenCheck,
    options: {},
    type: 'VITAL_ASSESS',
    isApplicable: check => check.id === 'BLOOD_PRESSURE_BOTH',
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  LUNG_SOUND: {
    title: 'Lung Sound',
    label: 'If lung sound is',
    component: LungSoundCheck,
    options: {
      query: require('~lib/common/queries/Sound').fetch,
      queryProp: 'sounds',
    },
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  HEART_SOUND: {
    title: 'Heart Sound',
    label: 'If heart sound is',
    component: HeartSoundCheck,
    options: {
      query: require('~lib/common/queries/Sound').fetch,
      queryProp: 'sounds',
    },
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  LUNG_SOUND_ASSESSED: {
    title: 'Lung Sound',
    label: 'If lung sounds were checked',
    component: LungSoundAssessedCheck,
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  HEART_SOUND_ASSESSED: {
    title: 'Heart Sound',
    label: 'If heart sound was checked',
    component: HeartSoundAssessedCheck,
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  ANIMATION_REPEATING: {
    title: 'Behavior',
    label: 'If behavior is',
    component: BehaviourCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  ANIMATION: {
    title: 'Behavior',
    label: 'If behavior is',
    component: BehaviourCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  MEDICATION_ADMINISTERED: {
    title: 'Medication',
    label: 'If a medication was given',
    component: MedicationGivenCheck,
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: (caseConf) => caseConf.dosageForms && caseConf.dosageForms.length,
    category: 'Medications',
    superCategory: 'Medications',
  },
  MEDICATION_GROUP: {
    title: 'Medication',
    label: 'If total medication',
    component: MedicationGroupCheck,
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: (caseConf) => caseConf.dosageForms && caseConf.dosageForms.length,
    category: 'Medications',
    superCategory: 'Medications',
  },
  INFUSION_SETUP: {
    title: 'Infusion',
    label: 'If infusion initiated/titrated',
    component: InfusionSetupCheck,
    types: ['INFUSION_MEDICATION_SETUP', 'INFUSION_FLUID_SETUP'],
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: caseConf => infusionCheckRecommended(caseConf),
    category: 'IV',
    superCategory: 'IV',
  },
  INFUSION: {
    title: 'Infusion',
    label: 'If infusion status',
    component: InfusionCheck,
    types: ['INFUSION_MEDICATION', 'INFUSION_FLUID'],
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: caseConf => infusionCheckRecommended(caseConf),
    category: 'IV',
    superCategory: 'IV',
  },
  INFUSION_RATE: {
    title: 'Infusion',
    label: 'If infusion rate',
    component: InfusionRateCheck,
    types: ['INFUSION_MEDICATION_RATE', 'INFUSION_FLUID_RATE'],
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: caseConf => infusionCheckRecommended(caseConf),
    category: 'IV',
    superCategory: 'IV',
  },
  INFUSION_ACTION_TYPE: {
    title: 'Infusion function',
    label: 'If infusion adjusted',
    component: InfusionFunctionCheck,
    types: ['INFUSION_MEDICATION_ACTION_TYPE', 'INFUSION_FLUID_ACTION_TYPE'],
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: caseConf => infusionCheckRecommended(caseConf),
    category: 'IV',
    superCategory: 'IV',
  },
  PACING_DEFIB: {
    title: 'Defibrillation',
    label: 'If patient has been defibrillated',
    component: DefibCheck,
    type: 'PACING',
    options: {},
    isApplicable: check => check.id === 'SYNC' || check.id === 'DEFIBRILLATION',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'DEFIBRILLATION'),
    category: 'Monitors',
    superCategory: 'Monitors',
  },
  COMPRESSION_ADMINISTERED: {
    title: 'CPR',
    label: 'Are compressions being administered',
    component: CompressionCheck,
    options: {},
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'CPR'),
    category: 'Procedures',
    superCategory: 'Procedures',
  },
  PACING_TRANSCUTANEOUS: {
    title: 'Pacing',
    label: 'If pacing',
    component: PacingCheck,
    type: 'PACING',
    options: {},
    isApplicable: check => check.id === 'TRANSCUTANEOUS',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PACING'),
    category: 'Monitors',
    superCategory: 'Monitors',
  },
  WAVEFORM: {
    title: 'Waveform',
    label: 'If the value of a waveform',
    component: VitalCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'waveform',
        placeholder: 'Select waveform',
        type: 'WAVEFORM',
        useDefault: true,
      },
    },
    category: 'Monitors',
    superCategory: 'Monitors',
  },
  VITAL_ASSESSED_TEMPERATURE: {
    title: 'Temperature',
    label: 'If temperature was taken',
    component: VitalAssessedCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        vitalKey: 'TEMPERATURE',
      },
    },
    type: 'VITAL_ASSESS',
    isApplicable: check => check.id === 'TEMPERATURE',
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  VITAL_ASSESSED_ETCO2: {
    title: 'ETCO2',
    label: 'If ETCO2 was checked',
    component: VitalAssessedCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        vitalKey: 'ETCO2',
        label: 'Was ETCO2 checked ?',
      },
    },
    type: 'VITAL_ASSESS',
    isApplicable: check => check.id === 'ETCO2',
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  VITAL_ASSESSED_GLUCOSE: {
    title: 'Respiratory Rate',
    label: 'If glucose was checked',
    component: VitalAssessedCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        vitalKey: 'GLUCOSE',
      },
    },
    type: 'LAB_RESULT_VIEWED',
    isApplicable: check => check.id === 'GLUCOSE',
    category: 'Patient Conditions',
    superCategory: 'Monitors',
  },
  VAGAL_MANEUVER: {
    title: 'Vagal Maneuver',
    label: 'If vagal maneuver was performed',
    component: VagalManeuverCheck,
    options: {
    },
    type: 'PROCEDURE',
    isApplicable: check => check.id === 'VAGAL_MANEUVER',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'VAGAL_MANEUVER'),
    category: 'Procedures',
    superCategory: 'Procedures',
  },
  PENLIGHT: {
    title: 'Penlight',
    label: 'If penlight was used',
    component: PenlightCheck,
    options: {
    },
    type: 'PENLIGHT',
    category: 'Procedures',
    superCategory: 'Assessment',
  },
  NASAL_SWAB: {
    title: 'Nasal Swab',
    label: 'If nasal swab was taken',
    component: NasalSwabCheck,
    options: {
    },
    type: 'PROCEDURE',
    isApplicable: check => check.id === 'NASAL_SWAB',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'NASAL_SWAB'),
    category: 'Procedures',
    superCategory: 'Procedures',
  },
  PHYSICAL_ASSESSMENT_ASSESSED: {
    title: 'Physical Assessment',
    label: 'If physical assessment was performed',
    component: PhysicalAssessmentCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  EDEMA: {
    types: ['MOULAGE', 'STRUCTURAL_MOULAGE'],
    title: 'Edema',
    label: 'If edema is',
    component: MoulageCheck,
    options: {
      props: {
        placeholder: 'variation',
        moulageCategory: 'EDEMA',
        title: 'Is edema at',
        useStructuralLocations: true,
      },
    },
    isApplicable: effect => moulages.find(moulage => moulage.category === 'EDEMA' && moulage.key === effect.value),
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  EYE_STATE: {
    title: 'Eye Dilation',
    label: 'If eye dilation is',
    component: VitalCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'eyeState',
        placeholder: 'Select eye',
        type: 'EYE_STATE',
        useDefault: true,
      },
    },
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  EYE_STATE_RESPONSIVE: {
    title: 'Eye Reactivity',
    label: 'If eye reactivity is',
    component: NonreactiveCheck,
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  CUSTOM_STATE: {
    title: 'Custom State',
    label: 'If a custom state is',
    component: CustomStateCheck,
    options: {},
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf.caseLogicDescription && caseConf.caseLogicDescription.customStates && caseConf.caseLogicDescription.customStates.length,
    category: 'Custom States',
    superCategory: 'Miscellaneous',
  },
  TRANSFER: {
    title: 'Transfer',
    label: 'If patient has been transferred',
    component: PatientTransferCheck,
    options: {},
    isRecommended: (caseConf) => caseConf._endLocation,
    category: 'Transport',
    superCategory: 'Procedures',
  },
  PERSONAL_PROTECTIVE_EQUIPMENT: {
    title: 'PPE',
    label: 'If PPE is applied',
    component: PPEAppliedCheck,
    options: {
      query: require('~lib/common/queries/Treatment').fetch,
      queryProp: 'treatments',
    },
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PERSONAL_PROTECTIVE_EQUIPMENT'),
    category: 'Procedures',
    superCategory: 'Assessment',
  },
  BED_RAIL: {
    title: 'Bed rail',
    label: 'If bedrail is',
    component: BedRailCheck,
    type: 'BED_RAIL',
    isRecommended: (caseConf) =>
      (caseConf._startLocation && ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'].includes(caseConf._startLocation.key)) ||
      (caseConf._endLocation && ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'].includes(caseConf._endLocation.key)),
    category: 'Procedures',
    superCategory: 'Procedures',
  },
  EXPOSED: {
    title: 'Expose',
    label: 'If patient clothes',
    component: ExposeCheck,
    type: 'EXPOSED',
    category: 'Procedures',
    superCategory: 'Procedures',
  },
  LAB_ASSET_VIEWED: {
    title: featureAvailabilityService.translateLabel('Media'),
    label: `If ${featureAvailabilityService.translateLabel('Media')} was reviewed`,
    component: LabMediaAssessedCheck,
    options: {
      query: require('~lib/common/queries/LabAsset').fetch,
      queryProp: 'labAssets',
    },
    type: 'LAB_ASSET_VIEWED',
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf._labAssets && caseConf._labAssets.find(t => get(t, 'options.assets', []).length),
    category: featureAvailabilityService.translateLabel('Media'),
    superCategory: 'Assessment',
  },
  COMPLEXION: {
    title: 'Skin condition',
    label: 'If skin condition is',
    component: ComplexionCheck,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  SPINAL_ASSESSMENT: {
    title: 'Spinal Assessment',
    label: 'If spine was assessed',
    component: SpinalAssessmentCheck,
    isRecommended: (caseConf) => (caseConf.spinalAssessments || []).length > 0,
    category: 'Patient Conditions',
    superCategory: 'Assessment',
  },
  CUSTOM_MENU: {
    title: 'Custom Menu',
    label: 'If custom menu selected',
    component: CustomMenuCheck,
    isRecommended: (caseConf) => (caseConf.customMenus || []).length > 0,
    category: 'Dialogue',
    superCategory: 'Miscellaneous',
  },
  UNSOLICITED_CALL: {
    title: 'Callee Unsolicited',
    label: 'If unsolicited callee dialogue was played',
    component: UnsolicitedCalleeCheck,
    options: {
    },
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf.dialogue && caseConf.dialogue.calls && caseConf.dialogue.calls.length,
    category: 'Dialogue',
    superCategory: 'Dialogue',
  },
};

const CHECK_CONTROLS = mapValues(CHECKS, check => ({
  ...check,
  control: makeVueControl(check.component, check.options),
}));

export default CHECK_CONTROLS;
